import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Plus, Calendar, MapPin, LogOut, User, Settings, ChevronRight, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

export default function DashboardPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFestival, setNewFestival] = useState({
    name: "",
    date: "",
    location: "",
    description: "",
    iban: "",
    bank_name: "",
    payment_reference: ""
  });

  useEffect(() => {
    if (token || user) {
      fetchFestivals();
    }
  }, [token, user]);

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchFestivals = async () => {
    try {
      const response = await axios.get(`${API}/festivals`, getAuthHeaders());
      setFestivals(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching festivals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFestival = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/festivals`, newFestival, getAuthHeaders());
      toast.success("Festival created successfully!");
      setShowCreateDialog(false);
      setNewFestival({ name: "", date: "", location: "", description: "", iban: "", bank_name: "", payment_reference: "" });
      fetchFestivals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create festival");
    }
  };

  const handleDeleteFestival = async (festivalId) => {
    if (!window.confirm("Are you sure you want to delete this festival?")) return;
    try {
      await axios.delete(`${API}/festivals/${festivalId}`, getAuthHeaders());
      toast.success("Festival deleted");
      fetchFestivals();
    } catch (error) {
      toast.error("Failed to delete festival");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="dashboard-logo">
            <Trophy className="w-8 h-8 text-[#FFD700]" />
            <span className="font-['Playfair_Display'] text-xl font-bold text-white">JackAndJill</span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && (
              <Link to="/admin" className="nav-link" data-testid="admin-link">
                <Settings className="w-5 h-5" />
              </Link>
            )}
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <User className="w-5 h-5" />
              <span className="hidden sm:inline">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
              My Festivals
            </h1>
            <p className="text-[#A1A1AA]">
              Manage your dance competition events
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-gold px-6 py-3 rounded-lg" data-testid="create-festival-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Festival
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Create New Festival</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateFestival} className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#A1A1AA]">Festival Name</Label>
                  <Input
                    value={newFestival.name}
                    onChange={(e) => setNewFestival({ ...newFestival, name: e.target.value })}
                    placeholder="Summer Swing Festival 2024"
                    className="mt-1 bg-black/20 border-white/10 text-white"
                    required
                    data-testid="festival-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Date</Label>
                    <Input
                      type="date"
                      value={newFestival.date}
                      onChange={(e) => setNewFestival({ ...newFestival, date: e.target.value })}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      required
                      data-testid="festival-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Location</Label>
                    <Input
                      value={newFestival.location}
                      onChange={(e) => setNewFestival({ ...newFestival, location: e.target.value })}
                      placeholder="Barcelona, Spain"
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      required
                      data-testid="festival-location-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Description (Optional)</Label>
                  <Input
                    value={newFestival.description}
                    onChange={(e) => setNewFestival({ ...newFestival, description: e.target.value })}
                    placeholder="The biggest swing dance event of the year"
                    className="mt-1 bg-black/20 border-white/10 text-white"
                    data-testid="festival-description-input"
                  />
                </div>
                <div className="pt-4 border-t border-white/10">
                  <h4 className="text-white font-medium mb-3">Payment Information (Optional)</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[#A1A1AA]">IBAN</Label>
                      <Input
                        value={newFestival.iban}
                        onChange={(e) => setNewFestival({ ...newFestival, iban: e.target.value })}
                        placeholder="ES12 3456 7890 1234 5678 9012"
                        className="mt-1 bg-black/20 border-white/10 text-white"
                        data-testid="festival-iban-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Bank Name</Label>
                      <Input
                        value={newFestival.bank_name}
                        onChange={(e) => setNewFestival({ ...newFestival, bank_name: e.target.value })}
                        placeholder="Santander"
                        className="mt-1 bg-black/20 border-white/10 text-white"
                        data-testid="festival-bank-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Payment Reference</Label>
                      <Input
                        value={newFestival.payment_reference}
                        onChange={(e) => setNewFestival({ ...newFestival, payment_reference: e.target.value })}
                        placeholder="Festival-2024"
                        className="mt-1 bg-black/20 border-white/10 text-white"
                        data-testid="festival-reference-input"
                      />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full btn-gold py-3" data-testid="submit-festival-btn">
                  Create Festival
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Festivals Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : festivals.length === 0 ? (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Trophy className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No festivals yet</h3>
            <p className="text-[#A1A1AA] mb-6">Create your first dance festival to get started</p>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-gold" data-testid="empty-create-btn">
              <Plus className="w-5 h-5 mr-2" />
              Create Festival
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {festivals.map((festival, index) => (
              <motion.div
                key={festival.festival_id}
                className="competition-card group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`festival-card-${festival.festival_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl font-bold text-white group-hover:text-[#FFD700] transition-colors">
                      {festival.name}
                    </h3>
                    {festival.description && (
                      <p className="text-[#A1A1AA] text-sm mt-1 line-clamp-2">{festival.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteFestival(festival.festival_id)}
                    className="text-[#52525B] hover:text-red-500 transition-colors p-2"
                    data-testid={`delete-festival-${festival.festival_id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2 text-sm text-[#A1A1AA] mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(festival.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {festival.location}
                  </div>
                </div>
                <Link to={`/festival/${festival.festival_id}`} data-testid={`open-festival-${festival.festival_id}`}>
                  <Button className="w-full btn-outline py-2">
                    Manage Festival
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
